import {
  ArrowDownIcon,
  ArrowUpIcon,
  BarsArrowDownIcon,
  EyeIcon,
  EyeSlashIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { PageOrderModal } from 'components/Modals/PageOrder'
import { SaveChanges } from 'components/SaveChanges'
import { IVisibleProp } from 'config'
import { CollateralReviewConfig, CollateralSection } from 'pages/Loan/PostClosing/CollateralReview/constants'
import { useEffect, useState } from 'react'
import { Prompt } from 'react-router-dom'
import { toast } from 'react-toastify'
import { getAdminConfig, setAdminConfig } from 'services'
import { Button, Tooltip } from 'stories/components'
import { confirm, convertToSanitize, prompt } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { FieldPropertyModal } from './FieldPropertyModal/FieldPropertyModal'

export const CollateralReviewConfiguration = () => {
  const [isLoading, setLoading] = useState(true)
  const [isChanged, setChanged] = useState(false)
  const [fields, setFields] = useState<Record<string, IVisibleProp>>({})
  const [sections, setSections] = useState<CollateralSection[]>([])
  const [editingField, setEditingField] = useState<{ key: string; field: IVisibleProp; sectionId?: number } | null>(
    null,
  )
  const [orderSectionId, setOrderSectionId] = useState<number | null>(null)
  const [previewValues, setPreviewValues] = useState<Record<string, any>>({})

  useEffect(() => {
    setLoading(true)
    getAdminConfig('collateralReview')
      .then((data: any) => {
        const config: CollateralReviewConfig = data as CollateralReviewConfig
        const loadedFields = (config?.fields as Record<string, IVisibleProp>) || {}
        setFields(loadedFields)
        setSections(config?.sections || [])
        const initialValues: Record<string, any> = {}
        Object.entries(loadedFields).forEach(([k, f]) => {
          if ((f as any).value !== undefined) initialValues[k] = (f as any).value
        })
        setPreviewValues(initialValues)
      })
      .finally(() => setLoading(false))
  }, [])

  const toggleVisible = (key: string) => {
    const newFields = cloneDeep(fields)
    newFields[key].visible = newFields[key].visible === false ? true : false
    setFields(newFields)
    setChanged(true)
  }

  const removeField = async (key: string, sectionId: number | null) => {
    const ok = await confirm('Are you sure you want to remove this field?')
    if (!ok) return
    const newFields = cloneDeep(fields)
    delete newFields[key]
    setFields(newFields)
    if (sectionId !== null) {
      const newSections = cloneDeep(sections)
      const section = newSections.find((s) => s.id === sectionId)
      if (section) section.fields = section.fields.filter((k) => k !== key)
      setSections(newSections)
    }
    setChanged(true)
  }

  const openEdit = (key: string) => {
    setEditingField({ key, field: cloneDeep(fields[key]) })
  }

  const openAddField = () => {
    setEditingField({ key: '', field: { inputType: 'text', title: '' } })
  }

  const openAddFieldInSection = (sectionId: number) => {
    setEditingField({ key: '', field: { inputType: 'text', title: '' }, sectionId })
  }

  const addSection = async () => {
    const title: any = await prompt('Section title')
    if (!title) return
    setSections((prev) => [...prev, { id: Date.now(), title, fields: [] }])
    setChanged(true)
  }

  const editSectionTitle = async (idx: number) => {
    const title: any = await prompt('Section title', { value: sections[idx].title })
    if (title === false) return
    const newSections = cloneDeep(sections)
    newSections[idx].title = title
    setSections(newSections)
    setChanged(true)
  }

  const moveSection = (idx: number, direction: number) => {
    const newSections = [...sections]
    newSections.splice(idx + direction, 0, newSections.splice(idx, 1)[0])
    setSections(newSections)
    setChanged(true)
  }

  const removeSection = async (idx: number) => {
    const ok = await confirm('Are you sure you want to remove this section? Fields will become ungrouped.')
    if (!ok) return
    setSections((prev) => prev.filter((_, i) => i !== idx))
    setChanged(true)
  }

  const onModalClose = (key: string, updated: IVisibleProp | null) => {
    const sectionId = editingField?.sectionId
    setEditingField(null)
    if (!updated) return
    const finalKey = key || convertToSanitize(updated.title || '') || `field_${Date.now()}`
    setFields((prev) => {
      const newFields = cloneDeep(prev)
      newFields[finalKey] = updated
      return newFields
    })
    if (sectionId !== undefined) {
      setSections((prev) => {
        const newSections = cloneDeep(prev)
        const section = newSections.find((s) => s.id === sectionId)
        if (section && !section.fields.includes(finalKey)) section.fields.push(finalKey)
        return newSections
      })
    }
    setChanged(true)
  }

  const onCloseSectionOrderModal = (newInputs: { title: string }[] | null) => {
    if (newInputs && orderSectionId !== null) {
      const newSections = cloneDeep(sections)
      const section = newSections.find((s) => s.id === orderSectionId)
      if (section) section.fields = newInputs.map((item: any) => item.fieldKey)
      setSections(newSections)
      setChanged(true)
    }
    setOrderSectionId(null)
  }

  const onSave = async () => {
    setLoading(true)
    try {
      await setAdminConfig('collateralReview', { fields, sections })
      toast('Collateral Review configuration saved.', { type: 'info' })
      setChanged(false)
    } catch (e) {
      toast('Failed to save configuration.', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const ungroupedKeys = Object.keys(fields).filter((key) => !sections.some((s) => s.fields.includes(key)))

  const renderField = (key: string, sectionId: number | null) => {
    const field = fields[key]
    if (!field) return null

    const isVisible = field.visible !== false
    const VisibleIcon = isVisible ? EyeIcon : EyeSlashIcon

    const additionalElements = (
      <div className="flex">
        <Tooltip message={isVisible ? 'Hide' : 'Show'}>
          <span className="btn-icon" onClick={() => toggleVisible(key)}>
            <VisibleIcon className="w-4 h-4 text-gray-500" />
          </span>
        </Tooltip>

        <span className="btn-icon" onClick={() => openEdit(key)}>
          <PencilSquareIcon className="w-4 h-4 text-gray-500" />
        </span>

        <span className="btn-icon" onClick={() => removeField(key, sectionId)}>
          <TrashIcon className="w-4 h-4 text-red-500" />
        </span>
      </div>
    )

    return (
      <div key={key} className={`grid grid-cols-1 items-center mb-2 ${isVisible ? 'opacity-100' : 'opacity-30 z-10'}`}>
        <RenderInput
          Key={key}
          input={{ ...field, value: previewValues[key], additionalElements } as any}
          onChange={(k: string, v: any) => {
            setPreviewValues((prev) => ({ ...prev, [k]: v }))
            setFields((prev) => {
              const newFields = cloneDeep(prev)
              if (newFields[k]) (newFields[k] as any).value = v
              return newFields
            })
            setChanged(true)
          }}
        />
      </div>
    )
  }

  const renderSection = (section: CollateralSection, sectionIdx: number) => (
    <div key={section.id} className="mb-4">
      <RenderInput
        Key={`section-${section.id}`}
        input={{
          inputType: 'section',
          title: section.title,
          additionalElements: (
            <div className="flex gap-2">
              <Tooltip message="Add a new field in this section">
                <span className="btn-icon" onClick={() => openAddFieldInSection(section.id)}>
                  <PlusIcon className="w-4 h-4" />
                </span>
              </Tooltip>

              <Tooltip message="Update field order">
                <span className="btn-icon" onClick={() => setOrderSectionId(section.id)}>
                  <BarsArrowDownIcon className="w-4 h-4" />
                </span>
              </Tooltip>

              {sectionIdx > 0 && (
                <span className="btn-icon" onClick={() => moveSection(sectionIdx, -1)}>
                  <ArrowUpIcon className="w-4 h-4" />
                </span>
              )}
              {sectionIdx < sections.length - 1 && (
                <span className="btn-icon" onClick={() => moveSection(sectionIdx, 1)}>
                  <ArrowDownIcon className="w-4 h-4" />
                </span>
              )}

              <span className="btn-icon" onClick={() => editSectionTitle(sectionIdx)}>
                <PencilSquareIcon className="w-4 h-4" />
              </span>
              <span className="btn-icon !text-red-500" onClick={() => removeSection(sectionIdx)}>
                <TrashIcon className="w-4 h-4" />
              </span>
            </div>
          ),
        }}
        onChange={() => {}}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-2">
        {section.fields.map((key) => renderField(key, section.id))}
      </div>
    </div>
  )

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      <Prompt when={isChanged} message={`You've made some changes!\nAre you sure want to leave without Saving?`} />

      <div>
        {sections.map(renderSection)}

        {ungroupedKeys.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-2">
            {ungroupedKeys.map((key) => renderField(key, null))}
          </div>
        )}
      </div>

      <div className="flex flex-row justify-center gap-4 mt-4">
        <Button link onClick={openAddField}>
          <span className="flex items-center gap-1">
            <PlusIcon className="w-4 h-4" />
            Add New Field
          </span>
        </Button>
        <Button link onClick={addSection}>
          <span className="flex items-center gap-1">
            <PlusIcon className="w-4 h-4" />
            Add New Section
          </span>
        </Button>
        <Button onClick={onSave} loading={isLoading} disabled={!isChanged}>
          Save
        </Button>
      </div>
      <SaveChanges show={isChanged} label="Save Changes" onSave={onSave} />

      {editingField && (
        <FieldPropertyModal
          key={editingField.key}
          fieldKey={editingField.key}
          field={editingField.field}
          allFields={fields}
          onClose={onModalClose}
        />
      )}

      {orderSectionId !== null &&
        (() => {
          const section = sections.find((s) => s.id === orderSectionId)
          const orderInputs = section
            ? section.fields.filter((k) => fields[k]).map((k) => ({ fieldKey: k, title: fields[k].title || k }))
            : []
          return <PageOrderModal key="sectionOrder" inputs={orderInputs} onClose={onCloseSectionOrderModal} />
        })()}
    </div>
  )
}
